import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { ministriesData } from '../data/ministriesData';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, MapPin, Mail, CheckCircle, Send } from 'lucide-react';
export function MinistryDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const ministry = ministriesData.find(m => m.id === id);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate API call
    console.log('Form submitted:', formData);
    setSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setSubmitted(false);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
    }, 3000);
  };
  if (!ministry) {
    return <Navigate to="/ministries" replace data-id="element-890" />;
  }
  const Icon = ministry.icon;
  return <Layout data-id="element-891">
      {/* Hero Section with Image - Reduced Height */}
      <div className="bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white relative overflow-hidden" data-id="element-892">
        <div className="absolute inset-0 opacity-30" data-id="element-893">
          <img src={ministry.image} alt={ministry.title} className="w-full h-full object-cover" data-id="element-894" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F44]/90 to-[#0A1F44]/60" data-id="element-895" />

        <div className="container-custom relative z-10" data-id="element-896">
          <Link to="/ministries" className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6" data-id="element-897">
            <ArrowLeft className="w-4 h-4" data-id="element-898" />
            Back to Ministries
          </Link>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex items-center gap-4" data-id="element-899">
            <div className="w-16 h-16 bg-[#C8102E] rounded-full flex items-center justify-center" data-id="element-900">
              <Icon className="w-8 h-8 text-white" data-id="element-901" />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold" data-id="element-902">
              {ministry.title}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* Ministry Info Cards */}
      <section className="py-8 bg-white border-b" data-id="element-903">
        <div className="container-custom" data-id="element-904">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-905">
            {ministry.meetingTime && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="flex items-center gap-4 p-6 bg-[#F5F7FA] rounded-xl" data-id="element-906">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#C8102E] shadow-sm" data-id="element-907">
                  <Clock className="w-6 h-6" data-id="element-908" />
                </div>
                <div data-id="element-909">
                  <p className="text-sm text-gray-500 font-medium" data-id="element-910">
                    Meeting Time
                  </p>
                  <p className="text-[#0A1F44] font-bold" data-id="element-911">
                    {ministry.meetingTime}
                  </p>
                </div>
              </motion.div>}

            {ministry.location && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="flex items-center gap-4 p-6 bg-[#F5F7FA] rounded-xl" data-id="element-912">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#C8102E] shadow-sm" data-id="element-913">
                  <MapPin className="w-6 h-6" data-id="element-914" />
                </div>
                <div data-id="element-915">
                  <p className="text-sm text-gray-500 font-medium" data-id="element-916">Location</p>
                  <p className="text-[#0A1F44] font-bold" data-id="element-917">
                    {ministry.location}
                  </p>
                </div>
              </motion.div>}

            {ministry.leader && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="flex items-center gap-4 p-6 bg-[#F5F7FA] rounded-xl" data-id="element-918">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#C8102E] shadow-sm" data-id="element-919">
                  <Mail className="w-6 h-6" data-id="element-920" />
                </div>
                <div data-id="element-921">
                  <p className="text-sm text-gray-500 font-medium" data-id="element-922">
                    Ministry Leader
                  </p>
                  <p className="text-[#0A1F44] font-bold" data-id="element-923">{ministry.leader}</p>
                </div>
              </motion.div>}
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-16 bg-white" data-id="element-924">
        <div className="container-custom" data-id="element-925">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-926">
            {/* Left Column - Description & Details */}
            <div className="lg:col-span-2 space-y-12" data-id="element-927">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4
            }} data-id="element-928">
                <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-929">
                  About This Ministry
                </h2>
                <div className="prose prose-lg max-w-none text-gray-600 leading-relaxed" data-id="element-930">
                  <p data-id="element-931">{ministry.description}</p>
                </div>
              </motion.div>

              {ministry.activities && <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} data-id="element-932">
                  <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-933">
                    What We Do
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-934">
                    {ministry.activities.map((activity, index) => <div key={index} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg" data-id="element-935">
                        <CheckCircle className="w-5 h-5 text-[#C8102E] shrink-0 mt-0.5" data-id="element-936" />
                        <span className="text-gray-700 font-medium" data-id="element-937">
                          {activity}
                        </span>
                      </div>)}
                  </div>
                </motion.div>}

              {ministry.targetAudience && <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-[#0A1F44] text-white p-8 rounded-2xl" data-id="element-938">
                  <h3 className="text-xl font-serif font-bold mb-4" data-id="element-939">
                    Who is this for?
                  </h3>
                  <p className="text-white/90 leading-relaxed" data-id="element-940">
                    {ministry.targetAudience}
                  </p>
                </motion.div>}
            </div>

            {/* Right Column - Contact Form */}
            <div className="lg:col-span-1" data-id="element-941">
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5
            }} className="bg-[#F5F7FA] p-8 rounded-2xl sticky top-24 border border-[#E8ECF0]" data-id="element-942">
                {submitted ? <div className="text-center py-8" data-id="element-943">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-944">
                      <CheckCircle className="w-8 h-8 text-green-600" data-id="element-945" />
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-2" data-id="element-946">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600" data-id="element-947">
                      Thank you for reaching out. The ministry leader will get
                      back to you shortly.
                    </p>
                  </div> : <>
                    <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-2" data-id="element-948">
                      Get Involved
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm" data-id="element-949">
                      Interested in joining or have questions? Send us a message
                      below.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-950">
                      <div className="grid grid-cols-2 gap-4" data-id="element-951">
                        <div data-id="element-952">
                          <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-953">
                            First Name *
                          </label>
                          <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors text-sm" data-id="element-954" />
                        </div>
                        <div data-id="element-955">
                          <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-956">
                            Last Name *
                          </label>
                          <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors text-sm" data-id="element-957" />
                        </div>
                      </div>

                      <div data-id="element-958">
                        <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-959">
                          Email Address *
                        </label>
                        <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors text-sm" data-id="element-960" />
                      </div>

                      <div data-id="element-961">
                        <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-962">
                          Phone Number
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors text-sm" data-id="element-963" />
                      </div>

                      <div data-id="element-964">
                        <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1" data-id="element-965">
                          Message *
                        </label>
                        <textarea id="message" name="message" required rows={4} value={formData.message} onChange={handleInputChange} className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors text-sm resize-none" data-id="element-966" />
                      </div>

                      <Button type="submit" className="w-full bg-[#0A1F44] hover:bg-[#081833] text-white" leftIcon={<Send className="w-4 h-4" data-id="element-968" />} data-id="element-967">
                        Send Message
                      </Button>
                    </form>
                  </>}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}